import React, { useState } from "react";
import { toast } from "sonner";
import { Copy, Check, Instagram } from "lucide-react";
import { api } from "../../lib/site";
import { getUtmSource } from "../../lib/utm";
import { getReferral } from "../../lib/referral";

export default function WaitlistForm({ variant = "light", testIdPrefix = "waitlist" }) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [loading, setLoading] = useState(false);
  const [entry, setEntry] = useState(null); // { referral_code, first_name, ... }
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/waitlist", {
        email,
        first_name: firstName || null,
        source: getUtmSource(),
        referred_by: getReferral(),
      });
      setEntry(data);
      toast.success("You're on the list.", { description: "Check your inbox for a welcome from Crowned Essence." });
      setEmail(""); setFirstName("");
    } catch (err) {
      toast.error("Could not join waitlist", { description: err?.response?.data?.detail || "Please try again shortly." });
    } finally {
      setLoading(false);
    }
  };

  const isDark = variant === "dark";
  const labelColor = isDark ? "text-ivory/60" : "text-matte/60";
  const inputBorder = isDark ? "border-ivory/30 text-ivory placeholder:text-ivory/40" : "border-matte/30 text-matte placeholder:text-matte/40";
  const btnClass = isDark
    ? "bg-gold text-matte hover:bg-gold-soft"
    : "bg-matte text-gold border border-gold/40 hover:bg-black";

  if (entry) {
    const shareUrl = `${window.location.origin}/waitlist?ref=${entry.referral_code}`;
    const shareText = "I just joined the Crowned Essence waitlist — a luxury subscription box celebrating Black-owned self-care. Join me:";

    const copyLink = async () => {
      try {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        toast.success("Link copied");
        setTimeout(() => setCopied(false), 2200);
      } catch {
        toast.error("Copy failed — long-press to copy the link");
      }
    };

    return (
      <div data-testid={`${testIdPrefix}-success`} className={`p-8 border ${isDark ? "border-gold/40 text-ivory" : "border-gold/50 text-matte"}`}>
        <div className="text-[11px] uppercase tracking-luxe-sm text-gold">Welcome to the Crown</div>
        <div className="mt-3 font-serif text-2xl md:text-3xl leading-tight">
          {entry.first_name ? `Your seat is reserved, ${entry.first_name}.` : "Your seat is reserved."}
        </div>
        <p className={`mt-2 text-sm ${isDark ? "text-ivory/60" : "text-matte/60"}`}>
          We just sent a welcome to your inbox.
        </p>

        <div className={`mt-8 pt-6 border-t ${isDark ? "border-ivory/15" : "border-matte/15"}`}>
          <div className="text-[11px] uppercase tracking-luxe-sm text-gold mb-2">Share with a Friend</div>
          <div className={`font-serif text-xl leading-tight ${isDark ? "text-ivory" : "text-matte"}`}>
            Invite someone worthy of the crown.
          </div>
          <p className={`mt-2 text-sm ${isDark ? "text-ivory/60" : "text-matte/60"}`}>
            When a friend joins with your link, we'll thank you with founding-member perks at launch.
          </p>

          <div className={`mt-5 flex items-center gap-0 border ${isDark ? "border-ivory/25" : "border-matte/25"}`}>
            <input
              data-testid={`${testIdPrefix}-share-link`}
              readOnly
              value={shareUrl}
              onFocus={(e) => e.target.select()}
              className={`flex-1 bg-transparent px-4 py-3 text-sm outline-none truncate ${isDark ? "text-ivory" : "text-matte"}`}
            />
            <button
              data-testid={`${testIdPrefix}-copy-link`}
              type="button"
              onClick={copyLink}
              className={`flex items-center gap-2 px-4 py-3 text-[10px] uppercase tracking-luxe-sm border-l ${isDark ? "border-ivory/25 bg-gold text-matte hover:bg-gold-soft" : "border-matte/25 bg-matte text-gold hover:bg-black"}`}
            >
              {copied ? <><Check size={14}/> Copied</> : <><Copy size={14}/> Copy</>}
            </button>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <a
              data-testid={`${testIdPrefix}-share-whatsapp`}
              href={`https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`}
              target="_blank" rel="noreferrer"
              className={`px-4 py-2 text-[10px] uppercase tracking-luxe-sm border transition-colors ${isDark ? "border-ivory/30 text-ivory hover:bg-ivory/10" : "border-matte/30 text-matte hover:bg-matte/5"}`}
            >WhatsApp</a>
            <a
              data-testid={`${testIdPrefix}-share-sms`}
              href={`sms:?&body=${encodeURIComponent(`${shareText} ${shareUrl}`)}`}
              className={`px-4 py-2 text-[10px] uppercase tracking-luxe-sm border transition-colors ${isDark ? "border-ivory/30 text-ivory hover:bg-ivory/10" : "border-matte/30 text-matte hover:bg-matte/5"}`}
            >Text</a>
            <a
              data-testid={`${testIdPrefix}-share-email`}
              href={`mailto:?subject=${encodeURIComponent("Join me on the Crowned Essence waitlist")}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`}
              className={`px-4 py-2 text-[10px] uppercase tracking-luxe-sm border transition-colors ${isDark ? "border-ivory/30 text-ivory hover:bg-ivory/10" : "border-matte/30 text-matte hover:bg-matte/5"}`}
            >Email</a>
            <a
              data-testid={`${testIdPrefix}-share-twitter`}
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
              target="_blank" rel="noreferrer"
              className={`px-4 py-2 text-[10px] uppercase tracking-luxe-sm border transition-colors ${isDark ? "border-ivory/30 text-ivory hover:bg-ivory/10" : "border-matte/30 text-matte hover:bg-matte/5"}`}
            >X / Twitter</a>
            <a
              data-testid={`${testIdPrefix}-share-instagram`}
              href="https://instagram.com/crownedessence5"
              target="_blank" rel="noreferrer"
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-[10px] uppercase tracking-luxe-sm border transition-colors ${isDark ? "border-ivory/30 text-ivory hover:bg-ivory/10" : "border-matte/30 text-matte hover:bg-matte/5"}`}
              onClick={copyLink}
              title="Link copied — paste it in your Story or DM"
            >
              <Instagram size={12}/> Instagram
            </a>
          </div>
          <p className={`mt-3 text-xs ${isDark ? "text-ivory/40" : "text-matte/40"}`}>
            Tip: On Instagram, tap "Instagram" — we'll copy your link so you can paste it in a Story or DM.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form data-testid={`${testIdPrefix}-form`} onSubmit={submit} className="w-full max-w-2xl">
      <div className="grid sm:grid-cols-5 gap-4">
        <div className="sm:col-span-2">
          <label className={`block text-[10px] uppercase tracking-luxe-sm mb-2 ${labelColor}`}>First Name</label>
          <input
            data-testid={`${testIdPrefix}-firstname-input`}
            value={firstName}
            onChange={(e)=>setFirstName(e.target.value)}
            placeholder="Optional"
            className={`w-full bg-transparent border-b ${inputBorder} py-3 outline-none focus:border-gold transition-colors text-base`}
          />
        </div>
        <div className="sm:col-span-3">
          <label className={`block text-[10px] uppercase tracking-luxe-sm mb-2 ${labelColor}`}>Email Address</label>
          <input
            data-testid={`${testIdPrefix}-email-input`}
            required
            type="email"
            value={email}
            onChange={(e)=>setEmail(e.target.value)}
            placeholder="you@essence.com"
            className={`w-full bg-transparent border-b ${inputBorder} py-3 outline-none focus:border-gold transition-colors text-base`}
          />
        </div>
      </div>
      <button
        data-testid={`${testIdPrefix}-submit`}
        type="submit"
        disabled={loading}
        className={`mt-8 px-10 py-4 text-[11px] uppercase tracking-luxe-sm transition-colors disabled:opacity-50 ${btnClass}`}
      >
        {loading ? "Joining…" : "Join the Waitlist"}
      </button>
      <p className={`mt-4 text-xs ${isDark ? "text-ivory/40" : "text-matte/40"}`}>By joining, you consent to receive occasional emails from Crowned Essence. Unsubscribe anytime.</p>
    </form>
  );
}
